import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  normalizeVisibleOAPColumns,
  officeAccountingAllFieldsList,
  officeAccountingDefaultConfiguration,
  OfficeAccountingColumnConfig,
} from "@/src/constants/officeAccountingTableConfig";
import { IOfficeAccountingTablePayload, IOfficeAccountingTableRow } from "@/src/types/officeAccounting";
import { getOfficeAccountingRowId } from "@/src/utils/officeAccountingMappers";
import { getUserConfigFromStorage } from "@/src/utils/userConfigStorage";

interface OfficeAccountingTableState {
  visibleColumns: OfficeAccountingColumnConfig[];
  hiddenColumns: OfficeAccountingColumnConfig[];
  data: IOfficeAccountingTableRow[];
  selectedRows: number[];
}

const userConfig = getUserConfigFromStorage();
const initialVisibleColumns = normalizeVisibleOAPColumns(
  userConfig?.OAPConfig?.OAPTable ?? officeAccountingDefaultConfiguration,
);

const initialState: OfficeAccountingTableState = {
  visibleColumns: initialVisibleColumns,
  hiddenColumns: officeAccountingAllFieldsList.filter(
    (col) => !initialVisibleColumns.some((visibleCol) => visibleCol.id === col.id),
  ),
  data: [],
  selectedRows: [],
};

const officeAccountingTableSlice = createSlice({
  name: "officeAccountingTable",
  initialState,
  reducers: {
    setData(state, action: PayloadAction<IOfficeAccountingTablePayload>) {
      state.data = action.payload.data;
    },
    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const colId = action.payload;
      const isVisible = state.visibleColumns.some((col) => col.id === colId);
      if (isVisible) {
        const col = state.visibleColumns.find((col) => col.id === colId)!;
        state.visibleColumns = state.visibleColumns.filter((col) => col.id !== colId);
        state.hiddenColumns.push({ title: col.title, id: col.id });
      } else {
        let col = state.hiddenColumns.find((col) => col.id === colId);
        if (!col) col = officeAccountingAllFieldsList.find((c) => c.id === colId)!;
        state.hiddenColumns = state.hiddenColumns.filter((c) => c.id !== colId);
        state.visibleColumns.push({ title: col.title, id: col.id, width: col.width ?? 5 });
      }
    },
    setColumnWidth(state, action: PayloadAction<{ id: string; width: number }>) {
      const { id, width } = action.payload;
      const col = state.visibleColumns.find((c) => c.id === id);
      if (col) col.width = width;
    },
    setRowFieldValue(
      state,
      action: PayloadAction<{ studentInfoId: number; field: keyof IOfficeAccountingTableRow; value: string }>,
    ) {
      const { studentInfoId, field, value } = action.payload;
      const row = state.data.find((r) => getOfficeAccountingRowId(r) === studentInfoId);
      if (row) {
        (row[field] as string) = value;
      }
    },
    toggleRowSelection(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedRows.includes(id)) {
        state.selectedRows = state.selectedRows.filter((rowId) => rowId !== id);
      } else {
        state.selectedRows.push(id);
      }
    },
    toggleAllRows(state) {
      if (state.selectedRows.length === state.data.length) {
        state.selectedRows = [];
      } else {
        state.selectedRows = state.data.map((row) => getOfficeAccountingRowId(row));
      }
    },
    showAllColumns(state) {
      state.visibleColumns = [...officeAccountingAllFieldsList];
      state.hiddenColumns = [];
    },
    hideAllColumns(state) {
      state.hiddenColumns = [...officeAccountingAllFieldsList];
      state.visibleColumns = [];
    },
    setVisibleColumns(state, action: PayloadAction<OfficeAccountingColumnConfig[]>) {
      const normalized = normalizeVisibleOAPColumns(action.payload);
      state.visibleColumns = normalized;
      state.hiddenColumns = officeAccountingAllFieldsList.filter(
        (col) => !normalized.some((visibleCol) => visibleCol.id === col.id),
      );
    },
  },
});

export const {
  setData,
  toggleColumnVisibility,
  setColumnWidth,
  setRowFieldValue,
  toggleRowSelection,
  toggleAllRows,
  showAllColumns,
  hideAllColumns,
  setVisibleColumns,
} = officeAccountingTableSlice.actions;

export default officeAccountingTableSlice.reducer;
